import { getLivabilityBaseUrl } from './client'
import type { Apartment, ApartmentImage } from './types'

export type ApartmentImageVariant = 'full' | 'thumb'

/** Resolve livability proxy path or absolute URL with optional variant. */
export function resolveApartmentImageUrl(
  path: string | null | undefined,
  variant: ApartmentImageVariant = 'full'
): string | null {
  if (!path) return null

  const base = getLivabilityBaseUrl()
  const absolute = path.startsWith('http') ? path : `${base}${path}`
  const url = new URL(absolute)
  url.searchParams.set('variant', variant)
  return url.toString()
}

export function getApartmentThumbnailUrl(
  apartment: Pick<Apartment, 'primary_image_url' | 'images'>
): string | null {
  if (apartment.primary_image_url) {
    return resolveApartmentImageUrl(apartment.primary_image_url, 'thumb')
  }

  const ready = (apartment.images ?? []).filter(
    (img) => img.mirror_status === 'done' && img.url
  )
  const primary =
    ready.find((img) => img.is_primary) ?? ready.sort((a, b) => a.position - b.position)[0]
  return primary?.url
    ? resolveApartmentImageUrl(primary.url, 'thumb')
    : null
}

export function getGalleryImages(
  images: ApartmentImage[] | null | undefined
): ApartmentImage[] {
  return (images ?? [])
    .filter((img) => img.mirror_status === 'done' && img.url)
    .sort((a, b) => a.position - b.position)
}

export function getApartmentGalleryUrls(
  apartment: Pick<Apartment, 'images' | 'image_url' | 'primary_image_url'>
): string[] {
  const fromImages = getGalleryImages(apartment.images)
    .map((img) => resolveApartmentImageUrl(img.url, 'full'))
    .filter((url): url is string => url !== null)

  if (fromImages.length > 0) return fromImages

  const fallback = resolveApartmentImageUrl(
    apartment.primary_image_url ?? apartment.image_url,
    'full'
  )
  return fallback ? [fallback] : []
}
